import { AlertCircle, CheckCircle2, FileText, Loader2, X } from 'lucide-react';

import { Upload } from '@new/common/ui/Upload/Upload';
import { SectionHeading } from '@new/common/ui/SectionHeading/SectionHeading';
import { TranscriptFilesList } from '@new/features/transcripts/shared/components/TranscriptFilesList';
import type { CourseCategory, TranscriptFile } from '@new/features/transcripts/shared/types';
import { withForm } from '@new/common/hooks/form';

import { TranscriptCoursesEditor } from './TranscriptCoursesEditor';
import { reviewDefaultValues } from './reviewSchema';
import type { OfficialFileEntry } from './useOfficialTranscriptValidation';
import type { EditableCourseRow } from './courseRows';

export const OfficialTranscriptSection = withForm({
  defaultValues: reviewDefaultValues,
  props: {
    documentFiles: [] as TranscriptFile[],
    rows: [] as EditableCourseRow[],
    categories: [] as CourseCategory[],
    onRowsChange: (_updater: (rows: EditableCourseRow[]) => EditableCourseRow[]) => {},
    onReorderCourses: (_courseIds: number[]) => {},
    pendingFiles: [] as OfficialFileEntry[],
    onUploadOfficialFiles: (_files: File[]) => Promise.resolve(),
    onRemoveOfficialFile: (_key: string) => {},
    onRetryValidation: (_key: string) => {},
  },
  render: function OfficialTranscriptSectionRender({
    documentFiles,
    rows,
    categories,
    onRowsChange,
    onReorderCourses,
    pendingFiles,
    onUploadOfficialFiles,
    onRemoveOfficialFile,
    onRetryValidation,
  }) {
    return (
      <section className="flex shrink-0 flex-col gap-4 rounded-lg border border-(--neutral-black-NB200) bg-(--neutral-white-NW1000) p-4">
        <SectionHeading title="Official transcript & courses" />

        <div className="flex flex-col gap-2">
          <p className="m-0! [font-size:var(--fs-l5)]! font-semibold text-(--neutral-black-NB700)!">
            Documents ({documentFiles.length})
          </p>
          <TranscriptFilesList files={documentFiles} emptyText="No transcript documents yet." />
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <label className="[font-size:var(--fs-p4)]! font-medium text-(--neutral-black-NB1000)!">
              Official transcript file(s)
              <span className="ml-1 font-normal text-(--neutral-black-NB600)!">(optional)</span>
            </label>{' '}
            <p className="m-0! [font-size:var(--fs-l5)]! text-(--neutral-black-NB700)!">
              Upload the student&apos;s official transcript to verify that the courses they entered
              are accurate. Once uploaded, the transcript will be automatically analyzed and
              compared with the current course information.
            </p>
            <Upload
              accept=".pdf,.jpg,.jpeg,.png"
              multiple
              showFilePreview={false}
              onUpload={() => Promise.resolve()}
              onUploadMultiple={onUploadOfficialFiles}
              supportedFormatsText="Supported formats: PDF, JPG, PNG"
            />
          </div>

          {pendingFiles.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {pendingFiles.map((entry) => (
                <li
                  key={entry.key}
                  className="flex items-center gap-2 rounded-lg border-2 border-(--neutral-black-NB100) p-3"
                >
                  <FileText size={20} className="shrink-0 text-(--primary-O900)" />
                  <span className="flex-1 truncate [font-size:var(--fs-p4)]! font-medium text-(--neutral-black-NB1000)!">
                    {entry.file.name}
                  </span>

                  {entry.status === 'uploading' || entry.status === 'validating' ? (
                    <span className="flex items-center gap-1.5 shrink-0 [font-size:var(--fs-l5)]! text-(--neutral-black-NB600)!">
                      <Loader2 size={14} className="animate-spin" />
                      {entry.status === 'uploading' ? 'Uploading…' : 'Validating…'}
                    </span>
                  ) : null}

                  {entry.status === 'done' ? (
                    <span className="flex items-center gap-1.5 shrink-0 [font-size:var(--fs-l5)]! text-(--success-S700)!">
                      <CheckCircle2 size={14} />
                      Validated
                    </span>
                  ) : null}

                  {entry.status === 'failed' ? (
                    <span className="flex items-center gap-1.5 shrink-0 [font-size:var(--fs-l5)]! text-(--danger-D700)!">
                      <AlertCircle size={14} />
                      {entry.error ?? 'Failed'}
                      <button
                        type="button"
                        onClick={() => onRetryValidation(entry.key)}
                        className="cursor-pointer underline"
                      >
                        Retry
                      </button>
                    </span>
                  ) : null}

                  <button
                    type="button"
                    onClick={() => onRemoveOfficialFile(entry.key)}
                    aria-label={`Remove ${entry.file.name}`}
                    className="shrink-0 cursor-pointer rounded p-1 hover:bg-(--neutral-black-NB100)"
                  >
                    <X size={16} className="text-(--neutral-black-NB600)" />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <TranscriptCoursesEditor
          rows={rows}
          categories={categories}
          onRowsChange={onRowsChange}
          onReorder={onReorderCourses}
        />
      </section>
    );
  },
});
